/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SelectOption } from 'src/UIKit';
import { useApplicationsService } from 'src/modules/applications/services';
import { Application } from 'src/modules/applications/types';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import {
  useAdminCompaniesService,
  useAdminCompanyService,
  useUserCompaniesService,
} from '../services';
import { AdminCompany } from '../types';
import { useCreateAdminCompanyFormDialogController } from './CreateAdminCompanyFormDialogController';
import { useNavigateWithCompany } from 'src/hooks/useNavigateWithCompany';
import { switchCompany } from 'src/helpers/helpers';

const SEARCH_QUERY_PARAM = 'query';

export const useAdminCompaniesViewController = () => {
  const navigateWithCompany = useNavigateWithCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loading: applicationsLoading,
    list: applicationList,
    getApplications,
    changeApplicationsFilter,
    resetApplications,
  } = useApplicationsService();
  const {
    loading: companiesLoading,
    filter: companiesFilter,
    list: companyList,
    getCompanies,
    changeCompaniesFilter,
    resetCompanies,
  } = useAdminCompaniesService();
  const { deleteCompany } = useAdminCompanyService();
  const { currentCompanyId, getCompanies: getUserCompanies } = useUserCompaniesService();

  const createAdminCompanyFormDialogController = useCreateAdminCompanyFormDialogController();

  const initLoad = useCallback(async () => {
    try {
      changeApplicationsFilter({ limit: 100 });
      await Promise.all([getCompanies(), getApplications()]);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadCompanies = useCallback(async () => {
    try {
      await getCompanies();
    } catch (e) {
      console.log(e);
    }
  }, [getCompanies]);

  const reloadUserCompanies = useCallback(async () => {
    try {
      await getUserCompanies();
    } catch (e) {
      console.log(e);
    }
  }, [getUserCompanies]);

  const reloadCompaniesDebounce = useMemo(() => debounce(reloadCompanies, 500), [reloadCompanies]);

  useEffect(() => {
    return () => {
      (reloadCompaniesDebounce as unknown as { cancel?: () => void })?.cancel?.();
    };
  }, [reloadCompaniesDebounce]);

  const changeCompanyFilterForm = useCallback(
    async (filter: Partial<RootState['companies']['adminCompanyListFilter']>) => {
      // Back to the first page, the way the search box already does: a narrowed list is usually shorter than
      // the page the admin was standing on, and they would be looking at an empty grid.
      changeCompaniesFilter({ ...filter, page: 0, offset: 0 });
      await reloadCompanies();
    },
    [],
  );

  const changeCompaniesSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeCompaniesFilter({ query: query || undefined, page: 0, offset: 0 });
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (query) next.set(SEARCH_QUERY_PARAM, query);
        else next.delete(SEARCH_QUERY_PARAM);
        return next;
      }, { replace: true });
      reloadCompaniesDebounce();
    },
    [changeCompaniesFilter, reloadCompaniesDebounce, setSearchParams],
  );

  const changeCompaniesSorting = useCallback(async (s: GridSortModel) => {
    changeCompaniesFilter({ sort: `${s[0].field}_${s[0].sort}` });
    await reloadCompanies();
  }, []);

  const changeCompaniesPagination = useCallback(async (p: GridPaginationModel) => {
    changeCompaniesFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadCompanies();
  }, []);

  const removeCompany = useCallback(async (companyId: string) => {
    try {
      await deleteCompany(companyId);
      await reloadCompanies();
      await getUserCompanies();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const applicationOptions: SelectOption<string, string, Application>[] = useMemo(() => {
    return (
      applicationList?.items?.map(a => ({
        value: a.id,
        title: a.name,
        entity: a,
      })) || []
    );
  }, [applicationList]);

  useEffect(() => {
    const queryFromUrl = searchParams.get(SEARCH_QUERY_PARAM);
    if (queryFromUrl !== null && queryFromUrl !== '') {
      changeCompaniesFilter({ query: queryFromUrl, page: 0, offset: 0 });
    }
    initLoad();

    return () => {
      resetCompanies();
      resetApplications();
    };
  }, []);

  const searchQueryFromUrl = searchParams.get(SEARCH_QUERY_PARAM) ?? '';

  return {
    navigate: navigateWithCompany,
    isPageLoading: companiesLoading.getAdminCompanyList || applicationsLoading.getList,
    currentCompanyId,
    companiesFilter,
    companyList,
    searchValue: companiesFilter.query ?? searchQueryFromUrl,
    changeCompanyFilterForm,
    changeCompaniesSearchFilter,
    changeCompaniesSorting,
    changeCompaniesPagination,
    switchCompany,
    removeCompany,
    reloadCompanies,
    applicationOptions,
    createAdminCompanyFormDialogController: {
      ...createAdminCompanyFormDialogController,
      reloadCompanies,
      reloadUserCompanies,
      submit: async (company: AdminCompany, userData: any) => {
        await createAdminCompanyFormDialogController.submit(company, userData, reloadCompanies);
      },
    },
  };
};
